import { useState } from 'react'
import { Link } from 'react-router-dom'

import Icon from '@/components/ui/Icon'
import { sendInquiry } from '@/lib/inquiry'
import { useData } from '@/hooks/useLocale'

const FIELD =
  'min-w-0 rounded-xl bg-surface px-3.5 text-sm text-[#1e2330] outline-none focus:bg-white focus:shadow-[inset_0_0_0_1.5px_#1a73e8,0_0_0_4px_rgba(26,115,232,.12)]'

function Field({ label, ...props }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-xs font-medium text-muted">{label}</span>
      <input required className={`h-11 ${FIELD}`} {...props} />
    </label>
  )
}

/**
 * Demo request. Native constraint validation (`required`, `type=email`) decides
 * what is valid; `noValidate` swaps the browser bubbles for the design's
 * single error line.
 */
export default function ContactForm() {
  const { FORM } = useData()
  const [valid, setValid] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(null)

  async function submit(event) {
    event.preventDefault()
    const form = event.currentTarget

    if (!form.checkValidity()) {
      setError(FORM.errors.missing)
      return
    }

    const data = new FormData(form)
    // A person never fills a field they cannot see.
    if (data.get('website')) return
    data.delete('website')

    setBusy(true)
    try {
      const via = await sendInquiry(data, FORM.mailSubject(data.get('company')))
      setSent({
        firstName: data.get('name').trim().split(/\s+/)[0],
        email: data.get('email'),
        via,
      })
    } catch {
      setError(FORM.errors.failed)
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div
        role="status"
        className="flex flex-col items-center gap-3 px-3 py-14 text-center"
      >
        <span className="grid size-16 place-items-center rounded-full bg-[#e7f6ec] text-[#1f9d55]">
          <Icon name="check" size={28} />
        </span>
        <span className="text-[22px] font-medium">
          {FORM.sent.title(sent.firstName)}
        </span>
        <span className="max-w-85 text-[15px]/[1.6] text-muted">
          {sent.via === 'posted' ? FORM.sent.posted(sent.email) : FORM.sent.mailed}
        </span>
        <button
          type="button"
          onClick={() => {
            setSent(null)
            setValid(false)
          }}
          className="mt-2 h-[42px] cursor-pointer rounded-xl bg-surface px-[18px] text-sm font-medium text-body"
        >
          {FORM.sent.again}
        </button>
      </div>
    )
  }

  const { fields } = FORM

  return (
    <form
      noValidate
      onSubmit={submit}
      onChange={(event) => {
        setValid(event.currentTarget.checkValidity())
        setError('')
      }}
      className="flex flex-col gap-3.5"
    >
      <div className="flex flex-col gap-0.5">
        <span className="text-xl font-medium tracking-[-0.01em]">{FORM.title}</span>
        <span className="text-[13px] text-subtle">{FORM.subtitle}</span>
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2">
        <Field {...fields.name} name="name" autoComplete="name" />
        <Field {...fields.email} name="email" type="email" autoComplete="email" />
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <Field {...fields.company} name="company" autoComplete="organization" />
        <Field {...fields.phone} name="phone" type="tel" autoComplete="tel" />
      </div>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1.5 text-xs font-medium text-muted">
          {FORM.size.label}
        </legend>
        <div className="grid grid-cols-4 gap-1.5">
          {FORM.size.options.map((size) => (
            <label
              key={size}
              className="grid h-[38px] cursor-pointer place-items-center rounded-[10px] bg-surface text-sm font-medium text-body has-checked:bg-brand-tint has-checked:text-brand has-checked:inset-ring-[1.5px] has-checked:inset-ring-brand has-focus-visible:ring-2 has-focus-visible:ring-brand"
            >
              <input
                type="radio"
                name="size"
                value={size}
                defaultChecked={size === FORM.size.default}
                className="sr-only"
              />
              {size}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted">
          {fields.message.label}{' '}
          <span className="font-normal text-subtle">{fields.message.hint}</span>
        </span>
        <textarea
          name="message"
          placeholder={fields.message.placeholder}
          className={`min-h-19 resize-y py-3 leading-normal ${FIELD}`}
        />
      </label>

      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <p className="text-[13px]/normal text-muted">
        {FORM.notice.before}
        <Link
          to={FORM.notice.link.href}
          className="text-brand hover:text-brand-dark"
        >
          {FORM.notice.link.label}
        </Link>
        {FORM.notice.after}
      </p>

      <div className="flex flex-wrap items-center gap-3.5">
        <button
          type="submit"
          disabled={busy}
          className={`flex h-[46px] cursor-pointer items-center gap-2.5 rounded-xl px-5 text-[15px] font-semibold text-white ${valid ? 'bg-brand' : 'bg-[#9fc1f5]'}`}
        >
          {FORM.submit}
          <Icon name="arrowRight" />
        </button>
        <span role="alert" className="text-[13px] text-[#d93025]">
          {error}
        </span>
      </div>
    </form>
  )
}
